if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.items = Array.from(this.querySelectorAll('.product-media-modal__item'));
        this.counter = this.querySelector('[data-modal-counter]');
        this.status = this.querySelector('[data-modal-status]');
        this.previousButton = this.querySelector('[data-modal-previous]');
        this.nextButton = this.querySelector('[data-modal-next]');
        this.zoomButton = this.querySelector('[data-modal-zoom]');
        this.thumbnails = Array.from(this.querySelectorAll('[data-modal-thumbnail]'));
        this.activeIndex = 0;
        this.touchStart = null;
        this.querySelector('[role="dialog"]')?.addEventListener('pointerup', (event) => event.stopPropagation());

        const hasMultipleItems = this.items.length > 1;
        if (this.previousButton) this.previousButton.hidden = !hasMultipleItems;
        if (this.nextButton) this.nextButton.hidden = !hasMultipleItems;

        this.previousButton?.addEventListener('click', () => this.navigate(-1));
        this.nextButton?.addEventListener('click', () => this.navigate(1));
        this.zoomButton?.addEventListener('click', () => this.toggleZoom());
        this.thumbnails.forEach((thumbnail) => {
          thumbnail.addEventListener('click', () => this.setActive(Number(thumbnail.dataset.modalThumbnail), true));
        });
        this.addEventListener('keydown', (event) => this.onKeyDown(event));
        this.querySelector('[data-modal-track]')?.addEventListener('pointerdown', (event) => this.onPointerStart(event));
        this.querySelector('[data-modal-track]')?.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.querySelector('[data-modal-track]')?.addEventListener('pointerup', (event) => this.onPointerEnd(event));
        this.querySelector('[data-modal-track]')?.addEventListener('dragstart', (event) => event.preventDefault());
      }

      hide() {
        this.resetZoom();
        const gallery = document.getElementById(this.dataset.galleryId);
        const sectionId = gallery?.id.replace('MediaGallery-', '');
        const mediaId = this.items[this.activeIndex]?.dataset.mediaId;
        const activeOpener = gallery?.querySelector(
          `.product__media-item[data-media-id="${sectionId}-${mediaId}"] .product__media-toggle`
        );
        if (activeOpener) this.openedBy = activeOpener;
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        window.requestAnimationFrame(() => this.querySelector('.product-media-modal__toggle')?.focus());
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        const index = this.items.findIndex((item) => item.dataset.mediaId === mediaId);
        this.setActive(index < 0 ? 0 : index, false);
      }

      setActive(index, announce = true) {
        if (!this.items.length) return;
        this.activeIndex = (index + this.items.length) % this.items.length;
        this.resetZoom();
        this.items.forEach((item, itemIndex) => {
          const active = itemIndex === this.activeIndex;
          item.classList.toggle('active', active);
          item.hidden = !active;
          if (!active) item.querySelector('video')?.pause();
        });
        this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
          if (thumbnailIndex === this.activeIndex) thumbnail.setAttribute('aria-current', 'true');
          else thumbnail.removeAttribute('aria-current');
        });
        this.thumbnails[this.activeIndex]?.scrollIntoView({ block: 'nearest', inline: 'center' });
        const message = `Image ${this.activeIndex + 1} of ${this.items.length}`;
        if (this.counter) this.counter.textContent = `${this.activeIndex + 1} / ${this.items.length}`;
        if (announce && this.status) this.status.textContent = message;
        const gallery = document.getElementById(this.dataset.galleryId);
        const galleryMediaId = `${gallery?.id.replace('MediaGallery-', '')}-${this.items[this.activeIndex].dataset.mediaId}`;
        gallery?.setActiveMedia?.(galleryMediaId, false, { preventScroll: true, announce: false });
        this.items[this.activeIndex].querySelector('deferred-media')?.loadContent?.(false);
      }

      navigate(direction) {
        if (this.items.length > 1) this.setActive(this.activeIndex + direction);
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.target.matches('input, textarea, select, [contenteditable="true"]')) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigate(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate(1);
        }
      }

      onPointerStart(event) {
        if (this.classList.contains('is-zoomed') || !event.isPrimary || event.button > 0) return;
        this.touchStart = { x: event.clientX, y: event.clientY, time: performance.now(), captured: false };
      }

      onPointerMove(event) {
        if (!this.touchStart || this.touchStart.captured || !event.isPrimary) return;
        const dx = Math.abs(event.clientX - this.touchStart.x);
        const dy = Math.abs(event.clientY - this.touchStart.y);
        if (dx > 8 && dx > dy * 1.25) {
          event.currentTarget.setPointerCapture?.(event.pointerId);
          this.touchStart.captured = true;
        }
      }

      onPointerEnd(event) {
        if (!this.touchStart || !event.isPrimary) return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        const velocity = Math.abs(dx) / Math.max(performance.now() - this.touchStart.time, 1);
        this.touchStart = null;
        if (Math.abs(dx) <= Math.abs(dy) * 1.25) return;
        if (Math.abs(dx) >= 48 || velocity >= 0.35) this.navigate(dx < 0 ? 1 : -1);
      }

      toggleZoom() {
        const zoomed = this.classList.toggle('is-zoomed');
        this.zoomButton?.setAttribute('aria-pressed', String(zoomed));
        this.zoomButton?.setAttribute('aria-label', zoomed ? 'Reset image zoom' : 'Zoom image');
      }

      resetZoom() {
        this.classList.remove('is-zoomed');
        this.zoomButton?.setAttribute('aria-pressed', 'false');
        this.zoomButton?.setAttribute('aria-label', 'Zoom image');
      }
    }
  );
}
